import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, OrchestratorInvokeMessagePayload } from "@expert/shared-types";

import { type TimelineFeatures, useFeatures } from "@expert/features";
import {
    type GaiaWebSocketResponse,
    fallbackGaiaPayloadProps,
    gaiaWsEventBus,
    getExperimentName,
    getFallbackPartner,
    getSupportedLineOfBusiness,
    useGaiaStore,
    useGaiaWebsocket,
} from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useMemo } from "react";

import type { SendMessageProps } from "../shared-types";

import { addFailureMessage, addMessage, useTimelineStore } from "../state";

const ORCHESTRATOR_RESPONSE_TIMEOUT_MS = 20_000;

export function setupOrchestratorTimeout(correlationId: string, sessionId: string, logger: Logger): () => void {
    let cleanedUp = false;

    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        unsubSuccess();
        unsubError();
        clearTimeout(timer);
    };

    const unsubSuccess = gaiaWsEventBus.on("gaia_ws_orchestrator-invoke-success", (response: GaiaWebSocketResponse) => {
        if (response.correlationId !== correlationId) return;
        cleanup();
    });

    const unsubError = gaiaWsEventBus.on("gaia_ws_orchestrator-invoke-error", (response: GaiaWebSocketResponse) => {
        if (response.correlationId !== correlationId) return;
        cleanup();
    });

    const timer = setTimeout(() => {
        cleanup();
        logger.warn({ correlationId, sessionId }, "GAIA orchestrator response timed out");
        addFailureMessage(sessionId, "botFailure");
    }, ORCHESTRATOR_RESPONSE_TIMEOUT_MS);

    return cleanup;
}

export const useSendExpertAssistMessage = (loggerProp: Logger, gaiaPayloadProps: GaiaPayloadConfigs) => {
    const websocketObj = useGaiaWebsocket();
    const { subscriptionStatus } = useGaiaStore();
    const { dispatcher } = useReactAnalytics();
    const {
        features: { isReactiveCallContextV2Enabled, isSalesFAQEnabled },
    } = useFeatures<TimelineFeatures>();
    const { expertId, currentSessionId: sessionId, getTimelineState } = useTimelineStore();
    const { callSid, chatId, lob, partner } = getTimelineState(sessionId);

    const logger = useMemo(() => loggerProp.child({ module: "useSendExpertAssistMessage" }), [loggerProp]);

    const sendMessage = ({ id, metadata, text, type, source }: SendMessageProps) => {
        const messageId = id ?? crypto.randomUUID();
        const correlationId = crypto.randomUUID();
        const formattedPartnerForGaia = getFallbackPartner({ partner });
        const { areExperimentsEnabled, appName } =
            gaiaPayloadProps ?? fallbackGaiaPayloadProps(chatId ? "chat" : "voice");
        const experimentName = areExperimentsEnabled
            ? getExperimentName({
                  isReactiveCallContextV2Enabled,
                  isSalesFAQEnabled,
                  lob,
              })
            : undefined;

        const lineOfBusiness = getSupportedLineOfBusiness({ partner: formattedPartnerForGaia, lob });
        const payload: OrchestratorInvokeMessagePayload = {
            action: "orchestrator-invoke",
            appName: appName,
            message: text,
            messageId,
            messageType: "Search",
            partner: formattedPartnerForGaia,
            sessionId: chatId ?? callSid ?? sessionId,
            sessionGroupId: sessionId,
            userId: expertId,
            userType: "expert",
            correlationId,
            experimentName,
            lineOfBusiness,
        };

        const messageText = metadata?.helpMeNowText ?? text;
        addMessage(sessionId, {
            id: messageId,
            text: messageText,
            type,
            isUnread: false,
            metadata,
            status: "active",
        });

        const analyticsInfo = {
            expertAssistAppName: "expert-assist",
            expertAssistExperimentName: experimentName,
            partner,
            messageId,
            messageType: type,
            source,
        };

        if (!websocketObj) {
            logger.error({ payload, websocketObj }, "There is no websocket obj");
            addFailureMessage(sessionId, "connectionFailure");
            return;
        }

        if (subscriptionStatus[sessionId] !== "subscribed") {
            // TODO: Currently, this attempts to resubscribe + sends error message
            // Should we attempt to resubscribe + await + resend message?
            void websocketObj.subscribeSessionToGaia({
                sessionId,
                chatId,
                callSid,
                partner: formattedPartnerForGaia,
                sendJsonMessage: websocketObj.sendJsonMessage,
                logger,
            });
            logger.warn("Expert session not subscribed, subscribing now");
            addFailureMessage(sessionId, "connectionFailure");
            return;
        }
        websocketObj.sendJsonMessage(payload);
        setupOrchestratorTimeout(correlationId, sessionId, logger);
        void dispatcher.dispatchBusinessEvent("MessageSent", {
            senderType: "expert",
            ...analyticsInfo,
        });
        logger.info({ payload }, "Message sent to gaia");
        trackFullStoryEvent("ExpertAssistMessageSent", {
            sessionId,
            callSid,
            chatId,
            ...analyticsInfo,
        });
    };
    return sendMessage;
};
